import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar, faSpinner, faCheck, faFileDownload, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import './report-module.css'; // We'll create this file next


const ReportModule = ({ user, reportType, preset }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isReportComplete, setIsReportComplete] = useState(false);
  const [reportId, setReportId] = useState(null);
  const [reportUuid, setReportUuid] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [hasDownloadedOnce, setHasDownloadedOnce] = useState(false);
  const pollingIntervalRef = useRef(null);
  const progressIntervalRef = useRef(null);
  
  // Function to show fake progress animation for better UX
  const startProgressAnimation = () => {
    // Clear any existing interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    setProgressPercent(0);
    
    // Create an interval that increases the progress
    progressIntervalRef.current = setInterval(() => {
      setProgressPercent(prev => {
        // Slow down as it approaches 100%
        const increment = Math.max(1, 10 * (1 - prev / 100));
        const newValue = prev + increment;
        
        // Cap at 95% (final jump to 100% happens when complete)
        return Math.min(95, newValue);
      });
    }, 300);
  };
  
  // Function to download the report PDF
  const downloadReport = (isAutomatic = false) => {
    if (!reportUuid) {
      console.error('No report UUID available for download');
      return;
    }
    
    setIsDownloading(true);
    console.log(`Starting ${isAutomatic ? 'automatic' : 'manual'} download for report UUID: ${reportUuid}`);
    
    // Create an anchor element and trigger download
    const downloadUrl = `https://service3.szapfs.org/api/report_requests/file/${reportUuid}`;
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.setAttribute('download', `${reportType.replace(/\s+/g, '-').toLowerCase()}-report-${reportUuid.substring(0, 8)}.pdf`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // If this is the first automatic download, mark it as done
    if (isAutomatic) {
      setHasDownloadedOnce(true);
    }
    
    // Reset download state after a delay
    setTimeout(() => setIsDownloading(false), 1500);
  };
  
  // Effect to automatically start download when report is complete
  useEffect(() => {
    if (isReportComplete && reportUuid && !hasDownloadedOnce && !isDownloading) {
      // Add a small delay before starting the automatic download
      setTimeout(() => {
        downloadReport(true);
      }, 500);
      
      // After a couple seconds, revert ONLY the button text while keeping download button
      setTimeout(() => {
        // We create a partial reset that keeps the download button
        setIsReportComplete(false);
        setStatus(null);
        // Note: We do NOT reset reportUuid or reportId, keeping the download button visible
        console.log('Reset main button text after completion, keeping download button');
      }, 2000); // Show complete state for 2 seconds as requested
    }
  }, [isReportComplete, reportUuid, hasDownloadedOnce, isDownloading]);

  // Function to check report status using the targeted endpoint
  const checkReportStatus = async () => {
    console.log('Checking report status...', { reportType, preset });
    
    if (!reportType || !preset) {
      console.log('Missing reportType or preset for status check');
      return;
    }
    
    try {
      // Use the exact endpoint requested
      const url = `https://service3.szapfs.org/api/report_requests/targeted?reportType=${encodeURIComponent(reportType)}&preset=${encodeURIComponent(preset)}`;
      console.log('Polling URL:', url);
      
      const response = await fetch(url);
      const result = await response.json();
      
      console.log('Report status check result:', result);
      
      if (result.success) {
        console.log('REPORT COMPLETE! 🎉', result.data);
        
        // Set progress to 100% when complete
        setProgressPercent(100);
        
        // Clear the progress animation interval
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        
        // Store the UUID for download
        if (result.data && result.data.uuid) {
          console.log('Report UUID captured:', result.data.uuid);
          setReportUuid(result.data.uuid);
        } else {
          console.warn('Report is complete but no UUID found:', result.data);
        }
        
        // Report is complete
        setIsReportComplete(true);
        setIsPolling(false);
        setStatus({ success: true, message: 'Report completed!', complete: true });
        
        // Clear the polling interval
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          console.log('Polling stopped - report complete');
        }
      } else if (result.error) {
        console.error('Error from status endpoint:', result.error);
      } else {
        console.log('Report still processing...');
      }
    } catch (error) {
      console.error('Error checking report status:', error);
    }
  };
  
  // Start polling after successful submission with simplified logic
  useEffect(() => {
    // Only start polling when we have a successful submission
    if (status?.success && !isReportComplete && !pollingIntervalRef.current) {
      console.log(`Starting polling for ${reportType} report with preset ${preset}`);
      setIsPolling(true);
      
      // Start progress animation for better UX
      startProgressAnimation();
      
      // Wait 2 seconds before starting to poll
      setTimeout(() => {
        // First check
        checkReportStatus();
        
        // Then set up interval (every 3 seconds to reduce load)
        pollingIntervalRef.current = setInterval(checkReportStatus, 3000);
      }, 2000);
    }
    
    // Clean up intervals on unmount or when dependencies change
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, [status?.success, isReportComplete, reportType, preset]);

  const handleClick = async () => {
    // If we have a reportUuid but not generating a new report, just trigger download
    if (reportUuid && (isReportComplete || status?.complete)) {
      downloadReport();
      return;
    }
    
    // If we're in the initial state with a download button showing, clear it
    if (reportUuid && !isReportComplete && !status?.complete) {
      console.log('Clearing existing download button before generating new report');
      setReportUuid(null);
    }
    
    // Don't process if already working
    if (isLoading || isPolling) {
      console.log('Ignoring click - already processing');
      return;
    }
    
    // Reset ALL states to ensure clean start and remove any download button
    console.log('Resetting all states before starting new request');
    setIsReportComplete(false);
    setHasDownloadedOnce(false);
    setProgressPercent(0);
    setReportUuid(null); // Clear any existing report UUID
    setReportId(null); // Clear any existing report ID
    setStatus(null); // Reset status
    
    // Clear any existing intervals
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    try {
      console.log('Starting report request process');
      setIsLoading(true);
      setStatus(null);
      
      // Current date time in MySQL format
      const dateTimeInitialized = new Date().toISOString().slice(0, 19).replace('T', ' ');
      
      // Extract PatientID from preset by stripping everything before and including the first "_"
      // If preset contains a "_", get everything after it, otherwise use the preset directly
      const extractedPatientID = preset.includes('_') ? preset.substring(preset.indexOf('_') + 1) : preset;
      console.log('Extracted PatientID:', extractedPatientID, 'from preset:', preset);
      
      // Prepare the data to send
      const reportData = {
        ReportType: reportType,
        Status: 'Pending',
        PatientID: extractedPatientID,
        PatientFirstName: '',
        PatientLastName: '',
        PatientEmail: '',
        DateTimeInitialized: dateTimeInitialized,
        Company: user.Company || '',
        Language: user.PreferredLanguage || 'English',
        Recipients: '[]', // Default empty array as string
        ClinicianID: String(user.id),
        Preset: preset,
        Details: '{}',
      };
      
      console.log('Sending report request:', reportData);
      
      const response = await fetch('https://service3.szapfs.org/api/reportinputreq', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reportData)
      });
      
      console.log('API response status:', response.status);
      const result = await response.json();
      
      if (response.ok) {
        console.log('🎯 Report request successful:', result);
        setReportId(result.id);
        setStatus({ success: true, message: `Report #${result.id} added successfully` });
        console.log('Status set to success - polling should start soon');
      } else {
        console.error('❌ Report request failed:', result);
        setStatus({ success: false, message: result.error || 'Failed to add report' });
      }
    } catch (error) {
      console.error('💥 Error sending report request:', error);
      setStatus({ success: false, message: 'Network error occurred' });
    } finally {
      console.log('Request process complete, setting isLoading to false');
      setIsLoading(false);
    }
  };

  // Get the appropriate CSS classes based on current state
  const getButtonClasses = () => {
    let baseClass = 'omrep-button';
    
    if (isLoading) {
      return `${baseClass} omrep-button-loading`;
    } else if (isPolling) {
      return `${baseClass} omrep-button-processing`;
    } else if (status?.complete) {
      return `${baseClass} omrep-button-complete`;
    } else if (status?.success === false) {
      return `${baseClass} omrep-button-failed`;
    } else {
      return `${baseClass} omrep-button-initial`;
    }
  };

  return (
    <div className="omrep-container">
      {/* Main Button */}
      <div 
        className={getButtonClasses()}
        onClick={handleClick}
      >
        {/* Progress bar for visual feedback */}
        {(isPolling || isLoading) && (
          <div 
            className="omrep-progress-bar" 
            style={{ width: `${progressPercent}%` }}
          />
        )}
        
        {/* Shimmer effect for processing state */}
        {isPolling && <div className="omrep-shimmer" />}
        
        <span className="omrep-icon">
          <FontAwesomeIcon 
            icon={
              isLoading || isPolling ? faSpinner :
              status?.complete ? faCheck :
              faChartBar
            }
            spin={isLoading || isPolling}
          />
        </span>
        
        {isLoading ? (
          'Submitting'
        ) : isPolling ? (
          'Processing'
        ) : status?.complete ? (
          'Complete' 
        ) : status?.success === false ? (
          'Failed'
        ) : (
          'Generate Report'
        )}
      </div>
      
      {/* Download Button - Only show when we have a reportUuid (keeps showing even after button resets) */}
      {reportUuid && (
        <div 
          className={`omrep-download ${isDownloading ? 'downloading' : ''}`}
          onClick={isDownloading ? null : () => downloadReport()}
          title="Download Report PDF"
        >
          <FontAwesomeIcon 
            icon={isDownloading ? faSpinner : faFileDownload} 
            spin={isDownloading}
          />
        </div>
      )}
    </div>
  );
};

export default ReportModule;
